import { Cpu, Cable, MonitorSmartphone, Wifi, Thermometer, CircuitBoard, Box, ShoppingCart } from 'lucide-react';
import { Button, Container, IconTile, Reveal, SectionHeading, CheckList } from '@/components/ui';
import { CtaBand, PageHero } from '@/components/sections';
import { usePageTitle } from '@/components/layout/SiteLayout';
import { ORDER_CATALOG } from '@scada-ink/shared/cloud';
import { orderLink } from '@/lib/site';

const SPECS = [
  { icon: Cpu, color: '#3b82f6', title: 'Compute', items: ['Industrial compute module, quad-core processor', '8 GB RAM and on-board storage', 'Software preinstalled and configured'] },
  { icon: Cable, color: '#7c3aed', title: 'Built-in I/O', items: ['2 isolated digital inputs, with millisecond part counting', '1 relay output and 2 analog inputs', 'Onboard I/O appears as ordinary tags'] },
  { icon: CircuitBoard, color: '#06b6d4', title: 'Serial & USB', items: ['1 × RS-232 serial port', '2 × RS-485 ports', 'USB for scanners, cameras and serial adapters'] },
  { icon: Wifi, color: '#16a34a', title: 'Networking', items: ['Gigabit Ethernet', 'Wi-Fi', 'Optional 4G cellular for remote sites'] },
  { icon: MonitorSmartphone, color: '#ca8a04', title: 'Display', items: ['HDMI output for line-side screens', 'Kiosk and TV display modes', 'Any browser on the network as a client'] },
  { icon: Thermometer, color: '#dc2626', title: 'Environment', items: ['Fanless design', 'DIN-rail mount', 'Sits in the control panel beside the PLC'] },
];

function ApplianceIllustration() {
  return (
    <div className="relative max-w-lg mx-auto">
      <div className="absolute inset-x-10 bottom-0 h-8 rounded-full bg-navy-900/20 blur-xl" />
      <div className="relative rounded-[28px] bg-gradient-to-b from-slate-100 to-slate-300 p-3 shadow-2xl">
        <div className="rounded-[20px] bg-gradient-to-b from-navy-800 to-navy-950 p-6">
          <div className="flex items-center justify-between">
            <div className="rounded-lg bg-white px-2.5 py-1.5">
              <img src="/scada-ink-logo.png" alt="" className="h-6 w-auto" />
            </div>
            <div className="flex gap-2">
              {['PWR', 'NET', 'RUN'].map((l, i) => (
                <div key={l} className="flex flex-col items-center gap-1">
                  <span className={`w-2 h-2 rounded-full ${i === 2 ? 'bg-brand-accent' : 'bg-status-running'} shadow-[0_0_8px_currentColor] text-status-running`} />
                  <span className="text-[8px] font-mono text-slate-400">{l}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="mt-8 grid grid-cols-5 gap-2">
            {['DI1', 'DI2', 'RLY', 'AI1', 'AI2'].map((p) => (
              <div key={p} className="flex flex-col items-center gap-1">
                <div className="w-full aspect-square rounded-md bg-emerald-700/80 border border-emerald-500/40 flex items-center justify-center">
                  <span className="w-2 h-2 rounded-full bg-slate-900" />
                </div>
                <span className="text-[8px] font-mono text-slate-400">{p}</span>
              </div>
            ))}
          </div>
          <div className="mt-5 flex items-end gap-3">
            {['RS-232', 'RS-485', 'RS-485', 'ETH', 'USB', 'HDMI'].map((p, i) => (
              <div key={i} className="flex flex-col items-center gap-1">
                <div className={`rounded-sm bg-slate-700 border border-slate-500 ${p === 'ETH' ? 'w-9 h-7' : p.startsWith('RS') ? 'w-10 h-5' : 'w-7 h-4'}`} />
                <span className="text-[8px] font-mono text-slate-400">{p}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="mx-auto h-3 w-3/4 bg-slate-400 rounded-b-md" />
    </div>
  );
}

export function HardwarePage() {
  usePageTitle('Hardware');
  return (
    <>
      <PageHero
        crumbs={[{ label: 'Hardware' }]}
        eyebrow="The Scada.Ink Appliance"
        title="Unbox it, plug it in, start monitoring"
        intro="Scada.Ink ships as a preconfigured appliance — software installed, I/O ready to wire, network ready. No servers to provision, no laptop left in the panel, no IT project."
        actions={
          <>
            <Button to={orderLink('order')} size="lg" arrow>
              <ShoppingCart size={16} /> Order appliances
            </Button>
            <Button to={orderLink('quote')} size="lg" variant="secondary">
              Request a quote
            </Button>
          </>
        }
        visual={<ApplianceIllustration />}
      />

      <section className="py-20 lg:py-24">
        <Container>
          <SectionHeading eyebrow="Specifications" title="Industrial where it counts" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {SPECS.map((s, i) => (
              <Reveal key={s.title} delay={i * 60}>
                <div className="glass-card h-full p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <IconTile icon={s.icon} color={s.color} size="sm" />
                    <h3 className="font-semibold">{s.title}</h3>
                  </div>
                  <CheckList items={s.items} color={s.color} />
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      <section className="py-20 bg-white border-y border-glass-border">
        <Container>
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <SectionHeading
                eyebrow="Expansion & accessories"
                title="Need more I/O? Add a card."
                intro="Expansion cards add digital and analog channels over a quick-connect cable — no rewiring and no downtime. Everything below can go on the same order."
              />
              <Button to={orderLink('order')} arrow>
                Build your order
              </Button>
            </div>
            <div className="glass-panel divide-y divide-glass-border">
              {ORDER_CATALOG.map((item) => (
                <div key={item.sku} className="flex items-start gap-4 p-4">
                  <Box size={18} className="text-brand-primary mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-baseline gap-x-2">
                      <span className="text-sm font-semibold">{item.name}</span>
                      <span className="text-[11px] font-mono text-text-muted">{item.sku}</span>
                    </div>
                    <p className="text-xs text-text-secondary mt-0.5">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Container>
      </section>

      <CtaBand title="Order hardware for your next line" body="Tell us how many machines and which controllers — we will size the appliances, expansion cards and software for you." />
    </>
  );
}
